/**
 * The chat, kept in this browser so a refresh or a closed tab does not lose it.
 *
 * A visitor who refreshes the host page used to lose the session id, the
 * transcript and the read token together, so a staff reply that arrived later
 * sat in the database, unread (cases HG88K, HMEW9; plan
 * docs/plans/2026-10-12-web-chat-refresh).
 *
 * `localStorage` with a 24-hour expiry (Alex, 2026-10-13), replacing
 * `sessionStorage` (2026-10-12): a visitor who closed the tab and opened the
 * chat again got a blank chat, and the staff reply stayed unread (case HNNPB).
 * The 24 hours run from the last message in the chat, so a staff reply restarts
 * them and opening the page does not; "Nova conversa" deletes the chat at once.
 */

import { validateReferralCode } from './referral';

const VERSION = 2;

export const CHAT_KEPT_FOR_MS = 24 * 60 * 60 * 1000;

export function chatSessionKey(clientId) {
  return `tf_concierge_chat_${clientId}`;
}

// A message the page was still building when it was stored cannot finish after
// a refresh: a partial stream is dropped, and an augmentation that will never
// arrive stops saying it is on its way.
function storableMessages(conversation) {
  return conversation
    .filter((message) => message && !message.isStreaming)
    .map((message) => {
      const { _streamKey, ...rest } = message;
      return rest.isAugmenting ? { ...rest, isAugmenting: false } : rest;
    });
}

function isValid(state) {
  return (
    state &&
    state.v === VERSION &&
    typeof state.sessionId === 'string' &&
    state.sessionId.length > 0 &&
    Array.isArray(state.conversation) &&
    typeof state.handledByHuman === 'boolean' &&
    typeof state.awaitingPerson === 'boolean' &&
    (state.conversationReadToken === null || typeof state.conversationReadToken === 'string') &&
    typeof state.continuedOnWhatsapp === 'boolean' &&
    Number.isFinite(state.lastMessageAt)
  );
}

/**
 * The stored chat for this client, or null when there is none or it expired.
 *
 * Storage the browser refuses (a blocked third-party iframe) is logged and
 * read as "nothing stored": the chat then works as it did before this module.
 * A stored value of another shape is logged as an error and deleted, so one
 * bad write cannot break every later page load.
 */
export function loadChatSession(clientId) {
  const key = chatSessionKey(clientId);
  let raw;
  try {
    raw = window.localStorage.getItem(key);
  } catch (error) {
    console.warn(
      '[chat-session] localStorage unavailable; the chat will not survive a refresh',
      error,
    );
    return null;
  }
  if (!raw) {
    return null;
  }
  let state;
  try {
    state = JSON.parse(raw);
  } catch (error) {
    state = null;
  }
  if (!isValid(state)) {
    console.error('[chat-session] stored chat has an unexpected shape; deleting it', { key });
    clearChatSession(clientId);
    return null;
  }
  if (Date.now() - state.lastMessageAt > CHAT_KEPT_FOR_MS) {
    clearChatSession(clientId);
    return null;
  }
  return state;
}

// When the chat last gained a message: kept from the stored chat while the
// conversation has the same session and length, so a page load or a token
// change does not restart the 24 hours.
function lastMessageAt(clientId, sessionId, messageCount) {
  try {
    const previous = JSON.parse(window.localStorage.getItem(chatSessionKey(clientId)));
    if (
      isValid(previous) &&
      previous.sessionId === sessionId &&
      previous.conversation.length === messageCount
    ) {
      return previous.lastMessageAt;
    }
  } catch (error) {
    // Unreadable or absent: this save starts the clock.
  }
  return Date.now();
}

/** Store the chat. Nothing is stored before the server has named the session. */
export function saveChatSession(clientId, state) {
  if (!state.sessionId) {
    return;
  }
  const conversation = storableMessages(state.conversation || []);
  const stored = {
    v: VERSION,
    sessionId: state.sessionId,
    conversation,
    handledByHuman: Boolean(state.handledByHuman),
    awaitingPerson: Boolean(state.awaitingPerson),
    conversationReadToken: state.conversationReadToken || null,
    continuedOnWhatsapp: Boolean(state.continuedOnWhatsapp),
    lastMessageAt: lastMessageAt(clientId, state.sessionId, conversation.length),
    ...(state.referralCode == null
      ? {}
      : { referralCode: validateReferralCode(state.referralCode) }),
  };
  try {
    window.localStorage.setItem(chatSessionKey(clientId), JSON.stringify(stored));
  } catch (error) {
    console.warn('[chat-session] could not store the chat; it will not survive a refresh', error);
  }
}

export function clearChatSession(clientId) {
  try {
    window.localStorage.removeItem(chatSessionKey(clientId));
  } catch (error) {
    console.warn('[chat-session] could not delete the stored chat', error);
  }
}

/**
 * Whether the page's launch (`?q=` or an answer link) was already handled in
 * the restored conversation. A refresh keeps the URL, and sending the same
 * question again would repeat it in front of the visitor and the team.
 */
export function launchAlreadyHandled(conversation, { initialQuery, initialAnswerId }) {
  if (!conversation || conversation.length === 0) {
    return false;
  }
  if (initialAnswerId) {
    return conversation.some(
      (message) => message.role === 'assistant' && message.answer_id === initialAnswerId,
    );
  }
  const query = String(initialQuery || '').trim();
  if (query) {
    return conversation.some((message) => message.role === 'user' && message.content === query);
  }
  return false;
}
